import React, { useEffect, useRef, useState } from 'react'
import CurietechAISvg from '~/assets/curietechai.svg'
import OnboardingPng from '~/assets/onboarding.png'
import { Button } from '~/components/common/Button'
import { CustomCard } from '~/components/common/CustomCard'
import { FigmaSwitch } from '~/components/common/FigmaSwitch'
import { Modal } from '~/components/modals/CurieModal'
import { Typography } from '~/components/common/Typography'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { bridge } from '~/services/bridge'
import { readFileAsText } from '~/services/onboardingService'
import { changeXmlSettings, updateOnboardStarted } from '~/services/organizationService'
import { BetterToast } from '~/utils'

type OnboardingStep = 'welcome' | 'settings'

function UploadIcon() {
  return (
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M8.25 12V5.8875L6.3 7.8375L5.25 6.75L9 3L12.75 6.75L11.7 7.8375L9.75 5.8875V12H8.25ZM4.5 15C4.0875 15 3.73438 14.8531 3.44063 14.5594C3.14688 14.2656 3 13.9125 3 13.5V11.25H4.5V13.5H13.5V11.25H15V13.5C15 13.9125 14.8531 14.2656 14.5594 14.5594C14.2656 14.8531 13.9125 15 13.5 15H4.5Z'
        fill='black'
        fillOpacity='0.45'
      />
    </svg>
  )
}

interface WelcomeStepProps {
  onGetStarted: () => void
}

function WelcomeStep({ onGetStarted }: WelcomeStepProps) {
  return (
    <div>
      {/* Header Image Section with decorative elements */}
      <div className='border-[#eaddff] border-b border-solid h-[200px] relative w-full'>
        <img src={OnboardingPng} alt='onboarding' className='w-full h-full object-cover' />
      </div>
      <div className='py-8 px-6 text-center w-full select-none'>
        <div>
          <Typography
            fontFamily='geist'
            fontSize='displaySm'
            fontWeight='normal'
            className='text-tokens-primary-2'
          >
            Welcome to new CurieTech
          </Typography>
        </div>
        <div className='mt-3'>
          <Typography
            fontFamily='geist'
            fontSize='bodyLg'
            className='text-tokens-neutral-text-secondary'
          >
            Talk to us, share your goal, and get your task done easier.
          </Typography>
        </div>
        <div className='mt-[52px]'>
          <Button variant='primary' onClick={onGetStarted} aria-label='Get started'>
            Get Started
          </Button>
        </div>
      </div>
    </div>
  )
}

function TrashIcon() {
  return (
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M14.25 4.5H11.25V3.75C11.25 3.35218 11.092 2.97064 10.8107 2.68934C10.5294 2.40804 10.1478 2.25 9.75 2.25H8.25C7.85218 2.25 7.47064 2.40804 7.18934 2.68934C6.90804 2.97064 6.75 3.35218 6.75 3.75V4.5H3.75C3.55109 4.5 3.36032 4.57902 3.21967 4.71967C3.07902 4.86032 3 5.05109 3 5.25C3 5.44891 3.07902 5.63968 3.21967 5.78033C3.36032 5.92098 3.55109 6 3.75 6H4.5V14.25C4.5 14.6478 4.65804 15.0294 4.93934 15.3107C5.22064 15.592 5.60218 15.75 6 15.75H12C12.3978 15.75 12.7794 15.592 13.0607 15.3107C13.342 15.0294 13.5 14.6478 13.5 14.25V6H14.25C14.4489 6 14.6397 5.92098 14.7803 5.78033C14.921 5.63968 15 5.44891 15 5.25C15 5.05109 14.921 4.86032 14.7803 4.71967C14.6397 4.57902 14.4489 4.5 14.25 4.5ZM8.25 3.75H9.75V4.5H8.25V3.75ZM12 14.25H6V6H12V14.25Z'
        fill='rgba(0,0,0,0.45)'
      />
    </svg>
  )
}

interface FileUploadZoneProps {
  fileName?: string
  label: string
  description: string
  onFileSelect: (file: File | null) => void
  onDelete?: () => void
  isUploaded?: boolean
  accept?: string
  maxSizeMB?: number
}

const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  fileName,
  label,
  description,
  onFileSelect,
  onDelete,
  isUploaded = false,
  accept = '.xml',
  maxSizeMB = 5
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > maxSizeMB * 1024 * 1024) {
        BetterToast.error(`File size exceeds ${maxSizeMB} MB`)
        return
      }
      onFileSelect(file)
    }
  }

  const handleCardClick = () => {
    if (!isUploaded) {
      fileInputRef.current?.click()
    }
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Reset file input value to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    onDelete?.()
  }

  if (isUploaded && fileName) {
    // Uploaded state: solid border with document icon and delete button
    return (
      <>
        <input
          ref={fileInputRef}
          type='file'
          className='hidden'
          accept={accept}
          onChange={handleFileChange}
        />
        <CustomCard
          icon={<XMLIcon />}
          title={
            <Typography
              fontFamily='geistMono'
              fontSize='bodyMdMedium'
              className='text-tokens-neutral-text-primary select-text'
              fontWeight='semibold'
            >
              {fileName}
            </Typography>
          }
          variant='solid'
          action={
            <button
              onClick={handleDelete}
              className='flex items-center justify-center size-[24px] hover:opacity-70 transition-opacity'
            >
              <TrashIcon />
            </button>
          }
          className='px-[36px] cursor-pointer'
        />
      </>
    )
  }

  // Not uploaded state: dashed border with upload icon
  return (
    <>
      <input
        ref={fileInputRef}
        type='file'
        className='hidden'
        aria-label='Upload maven settings.xml file'
        accept={accept}
        onChange={handleFileChange}
      />
      {/* this custom card hover state should change text color to text-tokens-status-link-3 */}
      <CustomCard
        icon={<UploadIcon />}
        title={
          <Typography
            fontFamily='geist'
            fontSize='bodyMdMedium'
            className='text-tokens-neutral-text-tertiary group-hover:text-tokens-status-link-3 select-none'
            fontWeight='medium'
          >
            {label}
          </Typography>
        }
        subtitle={
          <Typography
            fontFamily='geist'
            fontSize='labelSm'
            className='text-tokens-neutral-text-tertiary select-none'
          >
            {description}
          </Typography>
        }
        variant='dashed'
        onClick={handleCardClick}
        className='group px-[36px] cursor-pointer'
      />
    </>
  )
}

function XMLIcon() {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M15.75 5.0625V15.75C15.75 16.3467 15.5129 16.919 15.091 17.341C14.669 17.7629 14.0967 18 13.5 18V16.875C13.7984 16.875 14.0845 16.7565 14.2955 16.5455C14.5065 16.3345 14.625 16.0484 14.625 15.75V5.0625H12.375C11.9274 5.0625 11.4982 4.88471 11.1818 4.56824C10.8653 4.25178 10.6875 3.82255 10.6875 3.375V1.125H4.5C4.20163 1.125 3.91548 1.24353 3.7045 1.4545C3.49353 1.66548 3.375 1.95163 3.375 2.25V12.375H2.25V2.25C2.25 1.65326 2.48705 1.08097 2.90901 0.65901C3.33097 0.237053 3.90326 0 4.5 0L10.6875 0L15.75 5.0625ZM3.96788 13.3313H2.96325L2.03737 14.9501H1.99687L1.06088 13.3313H0.0135L1.39388 15.5621L0 17.8313H0.968625L1.92825 16.2394H1.96762L2.92387 17.8313H3.94537L2.53463 15.5903L3.96788 13.3324V13.3313ZM5.04112 17.8301V14.8376H5.08388L6.15488 17.2665H6.73537L7.79962 14.8365H7.84238V17.8301H8.64675V13.3313H7.74675L6.46425 16.2518H6.43613L5.1525 13.3313H4.24575V17.8301H5.04H5.04112ZM10.3399 17.0719H12.2479V17.8301H9.45V13.3313H10.3399V17.0719Z'
        fill='black'
        fillOpacity='0.88'
      />
    </svg>
  )
}

interface SecurityFileUploadContentProps {
  securityFile: File | null
  hasExistingSecurity: boolean
  onFileSelect: (file: File | null) => void
  onDelete?: () => void
}

const SecurityFileUploadContent: React.FC<SecurityFileUploadContentProps> = ({
  securityFile,
  hasExistingSecurity,
  onFileSelect,
  onDelete
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isUploaded = hasExistingSecurity || !!securityFile
  const fileName = securityFile?.name || (hasExistingSecurity ? 'settings_security.xml' : undefined)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        BetterToast.error('File size exceeds 5 MB')
        return
      }
      onFileSelect(file)
    }
  }

  const handleUploadClick = () => {
    if (!isUploaded) {
      fileInputRef.current?.click()
    }
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Reset file input value to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    onDelete?.()
  }

  return (
    <div className='px-[12px] py-[16px]'>
      <input
        ref={fileInputRef}
        type='file'
        className='hidden'
        accept='.xml'
        onChange={handleFileChange}
        aria-label='Upload settings_security.xml file'
      />

      {isUploaded && fileName ? (
        // Uploaded state
        <div className='flex items-center gap-[16px]'>
          <XMLIcon />
          <div className='flex-1 min-w-0'>
            <Typography
              fontFamily='geistMono'
              fontSize='bodySmMedium'
              className='text-tokens-neutral-text-primary select-text'
              fontWeight='semibold'
            >
              {fileName}
            </Typography>
          </div>
          {onDelete && (
            <button
              onClick={handleDelete}
              className='flex items-center justify-center size-[24px] hover:opacity-70 transition-opacity flex-shrink-0'
            >
              <svg
                width='18'
                height='18'
                viewBox='0 0 18 18'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M14.25 4.5H11.25V3.75C11.25 3.35218 11.092 2.97064 10.8107 2.68934C10.5294 2.40804 10.1478 2.25 9.75 2.25H8.25C7.85218 2.25 7.47064 2.40804 7.18934 2.68934C6.90804 2.97064 6.75 3.35218 6.75 3.75V4.5H3.75C3.55109 4.5 3.36032 4.57902 3.21967 4.71967C3.07902 4.86032 3 5.05109 3 5.25C3 5.44891 3.07902 5.63968 3.21967 5.78033C3.36032 5.92098 3.55109 6 3.75 6H4.5V14.25C4.5 14.6478 4.65804 15.0294 4.93934 15.3107C5.22064 15.592 5.60218 15.75 6 15.75H12C12.3978 15.75 12.7794 15.592 13.0607 15.3107C13.342 15.0294 13.5 14.6478 13.5 14.25V6H14.25C14.4489 6 14.6397 5.92098 14.7803 5.78033C14.921 5.63968 15 5.44891 15 5.25C15 5.05109 14.921 4.86032 14.7803 4.71967C14.6397 4.57902 14.4489 4.5 14.25 4.5ZM8.25 3.75H9.75V4.5H8.25V3.75ZM12 14.25H6V6H12V14.25Z'
                  fill='rgba(0,0,0,0.45)'
                />
              </svg>
            </button>
          )}
        </div>
      ) : (
        // Not uploaded state - clickable
        <div
          className='group flex items-center gap-[16px] cursor-pointer'
          onClick={handleUploadClick}
        >
          <div className='w-[24px] h-[24px] flex items-center justify-center flex-shrink-0'>
            <UploadIcon />
          </div>
          <div className='flex-1 min-w-0 flex flex-col gap-[4px]'>
            <Typography
              fontFamily='geist'
              fontSize='bodyMdMedium'
              className='text-tokens-neutral-text-tertiary group-hover:text-tokens-status-link-3 select-none'
              fontWeight='medium'
            >
              Upload Your Security Settings
            </Typography>
            <Typography
              fontFamily='geist'
              fontSize='labelSm'
              className='text-tokens-neutral-text-tertiary select-none'
            >
              XML file with maximum size of 5 MB
            </Typography>
          </div>
        </div>
      )}
    </div>
  )
}

interface SettingsUploadStepProps {
  onClose?: () => void
  refreshWorkspace: () => Promise<void>
  /** 'modal' (default) wraps the form in this dialog's own chrome — logo header
   *  bar, bordered footer, "Do it later". 'lock' drops all of it so the same
   *  form can sit in the content area of `WorkspaceSettingsXmlLock`, which
   *  supplies its own heading and has nothing to dismiss to. */
  variant?: 'modal' | 'lock'
}

export function SettingsUploadStep({
  onClose,
  refreshWorkspace,
  variant = 'modal'
}: SettingsUploadStepProps) {
  const { currentWorkspace, updateCurrentWorkspaceRuntimeSetting } = useWorkspaceContext()
  const isLockVariant = variant === 'lock'

  // Get existing uploaded files from org
  const existingSettingsXml = currentWorkspace?.org_runtime_setting?.settings_xml_raw_txt || ''
  const existingSecurityXml =
    currentWorkspace?.org_runtime_setting?.settings_security_xml_raw_txt || ''

  // Determine if files are already uploaded
  const hasExistingSettings = !!existingSettingsXml
  const hasExistingSecurity = !!existingSecurityXml

  const [settingsFile, setSettingsFile] = useState<File | null>(null)
  const [securityFile, setSecurityFile] = useState<File | null>(null)
  const [overrideSecurityPath, setOverrideSecurityPath] = useState(hasExistingSecurity)
  const [isLoading, setIsLoading] = useState(false)

  const isPlugin = bridge.isInPlugin()

  const handleDeleteSettings = async () => {
    if (settingsFile) {
      // Remove newly selected file (not yet saved)
      setSettingsFile(null)
    } else if (hasExistingSettings && currentWorkspace) {
      // Remove existing file from server
      try {
        await changeXmlSettings(currentWorkspace.uuid, {
          settings_xml_raw_txt: '',
          settings_security_xml_raw_txt: existingSecurityXml,
          settings_xml_file_name: '',
          settings_security_xml_file_name:
            currentWorkspace.org_runtime_setting?.settings_security_xml_file_name || ''
        })
        await refreshWorkspace()
        BetterToast.success('Settings file removed')
      } catch {
        BetterToast.error('Failed to remove settings file')
      }
    }
  }

  const handleDeleteSecurity = async () => {
    if (securityFile) {
      // Remove newly selected file (not yet saved)
      setSecurityFile(null)
    } else if (hasExistingSecurity && currentWorkspace) {
      // Remove existing file from server
      try {
        await changeXmlSettings(currentWorkspace.uuid, {
          settings_xml_raw_txt: existingSettingsXml,
          settings_security_xml_raw_txt: '',
          settings_xml_file_name:
            currentWorkspace.org_runtime_setting?.settings_xml_file_name || '',
          settings_security_xml_file_name: ''
        })
        await refreshWorkspace()
        BetterToast.success('Security file removed')
      } catch {
        BetterToast.error('Failed to remove security file')
      }
    }
  }

  const handleSave = async () => {
    if (!currentWorkspace) {
      BetterToast.error('Workspace not found')
      return
    }

    // Check if we need to upload settings file
    if (!settingsFile && !hasExistingSettings) {
      BetterToast.error('Please upload the Maven settings.xml file')
      return
    }

    if (overrideSecurityPath && !securityFile && !hasExistingSecurity) {
      BetterToast.error(
        'Please upload the settings_security.xml file or disable the override option'
      )
      return
    }

    setIsLoading(true)

    try {
      // Determine final content for each file
      let finalSettingsContent = existingSettingsXml
      let finalSecurityContent = existingSecurityXml

      // If new settings file is selected, read it
      if (settingsFile) {
        finalSettingsContent = await readFileAsText(settingsFile)
      }

      // If new security file is selected, read it
      if (securityFile) {
        finalSecurityContent = await readFileAsText(securityFile)
      }

      const settingsXmlFileName =
        settingsFile?.name || currentWorkspace.org_runtime_setting?.settings_xml_file_name || ''
      const settingsSecurityXmlFileName =
        securityFile?.name ||
        currentWorkspace.org_runtime_setting?.settings_security_xml_file_name ||
        ''

      // Update XML settings using existing org API
      await changeXmlSettings(currentWorkspace.uuid, {
        settings_xml_raw_txt: finalSettingsContent,
        settings_security_xml_raw_txt: finalSecurityContent,
        settings_xml_file_name: settingsXmlFileName,
        settings_security_xml_file_name: settingsSecurityXmlFileName
      })

      // Check if onboarding is complete (settings.xml is uploaded)
      const isOnboarded = !!finalSettingsContent

      // Optimistically reflect the save in context right away. A silent
      // refreshWorkspace() alone won't update `currentWorkspace` here when
      // the workspace uuid is unchanged (WorkspaceContext keeps the previous
      // object reference in that case), but the forced-onboarding gate needs
      // the fresh flags immediately so the modal can close and stay closed.
      updateCurrentWorkspaceRuntimeSetting({
        org_runtime_setting: {
          ...currentWorkspace.org_runtime_setting,
          settings_xml_raw_txt: finalSettingsContent,
          settings_security_xml_raw_txt: finalSecurityContent,
          settings_xml_file_name: settingsXmlFileName,
          settings_security_xml_file_name: settingsSecurityXmlFileName
        },
        ...(isOnboarded ? { onboarded: true, settings_xml_required: false } : {})
      })

      // Refresh the organization data to reconcile with the server
      await refreshWorkspace()

      if (isOnboarded) {
        BetterToast.success('Onboarding completed successfully!')
      } else {
        BetterToast.success('Settings uploaded successfully.')
      }

      onClose?.()
    } catch (error) {
      console.error('Error uploading files:', error)
      BetterToast.error(
        error instanceof Error ? error.message : 'Failed to upload files. Please try again.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleDoItLater = () => {
    onClose?.()
  }

  const handleLearnMore = () => {
    window.open(
      'https://docs.curietech.ai/docs/SecurityAndUserManagement/ManageWorkspace',
      '_blank'
    )
  }
  const disabled =
    isLoading ||
    (!settingsFile && !hasExistingSettings) ||
    (overrideSecurityPath && !securityFile && !hasExistingSecurity)

  const contentPadding = isLockVariant
    ? ''
    : isPlugin
      ? 'px-4 pt-6 pb-4'
      : 'px-[60px] pt-[48px] pb-[24px]'

  return (
    <div className={`flex flex-col h-full ${isLockVariant || isPlugin ? '' : 'min-h-[630px]'}`}>
      {/* Header — modal chrome only; the lock view has its own heading */}
      {!isLockVariant && (
        <div className='border-b border-[#eaeaea] px-[24px] py-0 w-full select-none'>
          <div className='flex h-[56px] items-center justify-center w-full'>
            <img src={CurietechAISvg} alt='CurieTech AI' />
          </div>
        </div>
      )}

      {/* Content */}
      <div className={`flex-1 flex flex-col gap-[40px] select-none ${contentPadding}`}>
        <div className='flex flex-col gap-[16px]'>
          <Typography fontFamily='geist' fontSize='titleSm' fontWeight='bold'>
            Upload your settings.xml file
          </Typography>
          <Typography fontFamily='geist' fontSize='bodySm' className='text-[rgba(0,0,0,0.65)]'>
            This is necessary to complete setup for your workspace.
          </Typography>
        </div>

        <div className='flex flex-col gap-[24px]'>
          <FileUploadZone
            fileName={settingsFile?.name || (hasExistingSettings ? 'settings.xml' : undefined)}
            label='Upload Your Maven Settings'
            description='XML file with maximum size of 5 MB'
            onFileSelect={setSettingsFile}
            onDelete={hasExistingSettings || settingsFile ? handleDeleteSettings : undefined}
            isUploaded={hasExistingSettings || !!settingsFile}
          />

          <div className='flex flex-col gap-[8px]'>
            <div className='bg-[rgba(189,189,189,0.08)] border border-[rgba(0,0,0,0.02)] rounded-[24px] px-[24px] py-[8px]'>
              <div className='px-[12px] py-[16px]'>
                <FigmaSwitch
                  checked={overrideSecurityPath}
                  onChange={setOverrideSecurityPath}
                  label='Override settings security path'
                />
              </div>

              {overrideSecurityPath && (
                <>
                  <div className='h-px w-full bg-[#eaeaea]' />
                  <SecurityFileUploadContent
                    securityFile={securityFile}
                    hasExistingSecurity={hasExistingSecurity}
                    onFileSelect={setSecurityFile}
                    onDelete={
                      hasExistingSecurity || securityFile ? handleDeleteSecurity : undefined
                    }
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        className={
          isLockVariant
            ? 'flex items-center justify-between pt-[32px] w-full select-none'
            : 'bg-[#fcfcfc] border-t border-[#eaeaea] flex items-center justify-between pl-[16px] pr-[32px] py-[16px] w-full select-none'
        }
      >
        <button
          onClick={handleLearnMore}
          className='flex items-center gap-2 text-tokens-neutral-text-secondary hover:text-tokens-neutral-text-primary transition-colors px-4 py-2'
        >
          <Typography fontFamily='geist' fontSize='bodyMd' fontWeight='medium'>
            Learn More
          </Typography>
          <svg
            width='16'
            height='16'
            viewBox='0 0 16 16'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M12 8.66667V12.6667C12 12.8435 11.9298 13.013 11.8047 13.1381C11.6797 13.2631 11.5101 13.3333 11.3333 13.3333H3.33333C3.15652 13.3333 2.98695 13.2631 2.86193 13.1381C2.7369 13.013 2.66667 12.8435 2.66667 12.6667V4.66667C2.66667 4.48986 2.7369 4.32029 2.86193 4.19526C2.98695 4.07024 3.15652 4 3.33333 4H7.33333C7.51014 4 7.67971 3.92976 7.80474 3.80474C7.92976 3.67971 8 3.51014 8 3.33333C8 3.15652 7.92976 2.98695 7.80474 2.86193C7.67971 2.7369 7.51014 2.66667 7.33333 2.66667H3.33333C2.8029 2.66667 2.29419 2.87738 1.91912 3.25245C1.54405 3.62753 1.33333 4.13623 1.33333 4.66667V12.6667C1.33333 13.1971 1.54405 13.7058 1.91912 14.0809C2.29419 14.456 2.8029 14.6667 3.33333 14.6667H11.3333C11.8638 14.6667 12.3725 14.456 12.7475 14.0809C13.1226 13.7058 13.3333 13.1971 13.3333 12.6667V8.66667C13.3333 8.48986 13.2631 8.32029 13.1381 8.19526C13.013 8.07024 12.8435 8 12.6667 8C12.4899 8 12.3203 8.07024 12.1953 8.19526C12.0702 8.32029 12 8.48986 12 8.66667ZM14 2H10.6667C10.4899 2 10.3203 2.07024 10.1953 2.19526C10.0702 2.32029 10 2.48986 10 2.66667C10 2.84348 10.0702 3.01305 10.1953 3.13807C10.3203 3.2631 10.4899 3.33333 10.6667 3.33333H12.3907L6.862 8.862C6.79975 8.92414 6.75003 8.99768 6.71569 9.07871C6.68134 9.15974 6.66304 9.24676 6.66304 9.33467C6.66304 9.42257 6.68134 9.50959 6.71569 9.59062C6.75003 9.67165 6.79975 9.74519 6.862 9.80733C6.92414 9.86958 6.99768 9.9193 7.07871 9.95364C7.15974 9.98799 7.24676 10.0063 7.33467 10.0063C7.42257 10.0063 7.50959 9.98799 7.59062 9.95364C7.67165 9.9193 7.74519 9.86958 7.80733 9.80733L13.3333 4.27933V6C13.3333 6.17681 13.4036 6.34638 13.5286 6.4714C13.6536 6.59643 13.8232 6.66667 14 6.66667C14.1768 6.66667 14.3464 6.59643 14.4714 6.4714C14.5964 6.34638 14.6667 6.17681 14.6667 6V2C14.6667 1.82319 14.5964 1.65362 14.4714 1.5286C14.3464 1.40357 14.1768 1.33333 14 1.33333C13.8232 1.33333 13.6536 1.40357 13.5286 1.5286C13.4036 1.65362 13.3333 1.82319 13.3333 2Z'
              fill='currentColor'
            />
          </svg>
        </button>

        <div className='flex gap-[8px] items-center'>
          {/* The lock view has nothing to dismiss to — its whole point is that
              the workspace is unusable until this upload lands. */}
          {!isLockVariant && (
            <Button variant='borderless' onClick={handleDoItLater} isDisabled={isLoading}>
              <Typography fontFamily='geist' fontSize='bodyMd' fontWeight='medium'>
                Do it later
              </Typography>
            </Button>
          )}
          <Button
            variant='primary'
            onClick={handleSave}
            isDisabled={disabled}
            aria-label='Save settings'
          >
            <Typography fontFamily='geist' fontSize='bodyMd' fontWeight='medium'>
              {isLoading ? 'Saving...' : 'Save'}
            </Typography>
          </Button>
        </div>
      </div>
    </div>
  )
}

interface OnboardingDialogProps {
  isOpen: boolean
  onClose: () => void
}

// The mandatory own-settings.xml requirement is NOT handled here — it locks the
// workspace via `WorkspaceSettingsXmlLock` in `ProtectedRoute`, so a gated user
// never reaches a route that could open this dialog. This is ordinary,
// dismissible onboarding.
const OnboardingDialog = ({ isOpen, onClose }: OnboardingDialogProps) => {
  const { refetch, currentWorkspace } = useWorkspaceContext()
  const [currentStep, setCurrentStep] = useState<OnboardingStep | null>(null)

  // Reset `currentStep` lazily here (when `isOpen` flips to `false`)
  // rather than synchronously on close. Synchronous reset would briefly
  // render an empty modal on the next open: `isOpen` flips to `true`
  // first, then this effect runs and re-derives the step.
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(null)
      return
    }
    if (!currentWorkspace) return
    setCurrentStep(currentWorkspace.onboard_started ? 'settings' : 'welcome')
  }, [isOpen, currentWorkspace])

  const handleGetStarted = () => {
    if (!currentWorkspace) return
    setCurrentStep('settings')
    updateOnboardStarted(currentWorkspace.uuid, true).then(refetch)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} className={'w-[640px]'}>
      {currentStep === 'welcome' && <WelcomeStep onGetStarted={handleGetStarted} />}
      {currentStep === 'settings' && (
        <SettingsUploadStep onClose={onClose} refreshWorkspace={refetch} />
      )}
    </Modal>
  )
}

export default OnboardingDialog
